import {
  Lock,
  Stack as StackIcon,
  Timer
} from '@phosphor-icons/react'

import {
  Box,
  Card,
  CardActionArea,
  CardMedia,
  Collapse,
  Grid,
  Skeleton,
  Typography
} from '@mui/material'
import PropTypes from 'prop-types'
import React from 'react'

import clsx from 'clsx'
import ContentTypeBadge from '../content-type-badge'
import IconTag from '../icon-tag'
import MtLogo from '../logos/mindtools_logo_primary_rgb.svg'
import { Stack } from './stack'
import { useStyles } from './style'
import { formatReadTime } from '../../helpers/read-time'
import { getContentImageUrl } from '../../helpers/content-image'
import { pluralize } from '../../helpers/plural'
/** A generic card that can be customised to display different content. */
const GeneralCard = (props) => {
  const {
    content,
    stack,
    onSelect,
    className,
    overrides = {},
    actions,
    expand = false,
    children,
    componentProps,
    testId,
    defaultImage,
    right = false,
    fullWidth,
    isPlaylist,
    playlistItemCount = 0,
    link,
    onMouseEnter,
    onMouseLeave,
    currentTab
  } = props
  const classes = useStyles(props)

  const contentImage = getContentImageUrl(content?.image, {
    width: 367,
    height: 202,
    fallback: defaultImage || MtLogo
  })

  // Undefined when the api could not resolve one, which is what drops the
  // separator and the clock below rather than rendering "null mins".
  const contentTime = formatReadTime(content?.readTime)

  const handleImageKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      onSelect && onSelect()
    }
  }

  return (
    <Box
      className={clsx(classes.root, className, overrides.root)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      data-test={testId || `card-${content?.friendlyUrl}'}`}
    >
      <Stack number={stack ?? 0}>
        <Card variant='elevation' classes={{ root: clsx(classes.card, overrides.card) }}>
          {!(fullWidth && right) &&
            (content
              ? (
                <CardMedia
                  onClick={onSelect}
                  onKeyDown={handleImageKeyDown}
                  image={contentImage}
                  title={content?.image?.altText ?? `${content?.title} thumbnail`}
                  aria-label={content?.image?.altText ?? `${content?.title} thumbnail`}
                  className={classes.image}
                  data-testid={`card-image-${testId}`}
                  data-test={`card-image-${content?.friendlyUrl}`}
                  role='button'
                  tabIndex={0}
                />
                )
              : (
                <Skeleton variant='rectangular' className={classes.image} />
                ))}
          <Box className={clsx(classes.summary, overrides.summary)}>
            <CardActionArea
              onClick={onSelect}
              disabled={!content}
              disableRipple
              classes={{
                focusHighlight: classes.actionAreaHighlighted
              }}
              data-testid={`card-action-area-${testId}`}
              data-test={`card-action-area-${content?.friendlyUrl ??
                'loading'}`}
              aria-label={content?.title ?? 'Loading...'}
            >
              <Grid
                container
                direction='row'
                alignItems='center'
                wrap='nowrap'
                justifyContent='space-between'
                className={classes.header}
                data-test={`card-header-${content?.friendlyUrl}'}`}
              >
                {content && (
                  <>
                    <Grid
                      xs
                      item
                      container
                      direction='row'
                      alignItems='center'
                      justifyContent='space-evenly'
                      wrap='nowrap'
                      className={classes.info}
                    >
                      {!isPlaylist && (
                        <Grid item xs>
                          <ContentTypeBadge
                            primaryType={content?.primaryType}
                            dataTest={`card-content-type-${content?.friendlyUrl}'}`}
                            classes={{ root: clsx(overrides.cardContentType) }}
                          />
                        </Grid>
                      )}
                      {content?.primaryType?.[0] === 'playlist' && (
                        <>
                          {!isPlaylist && (
                            <Grid item xs>
                              <Typography align='center'>&middot;</Typography>
                            </Grid>
                          )}
                          <Grid item xs>
                            <IconTag
                              className={
                                content.visibility === 'public' && currentTab !== 'my-playlists'
                                  ? classes.featuredPill
                                  : ''
                              }
                              icon={
                                content.visibility === 'private'
                                  ? (
                                    <Lock size={18} weight='duotone' />
                                    )
                                  : currentTab === 'my-playlists'
                                    ? (
                                      <Lock size={18} weight='duotone' />
                                      )
                                    : null
                              }
                              text={
                                content.visibility === 'private'
                                  ? 'Private'
                                  : currentTab === 'my-playlists'
                                    ? 'Private'
                                    : 'Featured'
                              }
                            />
                          </Grid>

                        </>
                      )}
                    </Grid>

                    <Grid item xs={7}>
                      {isPlaylist
                        ? (
                          <Typography
                            variant='body1'
                            className={clsx(classes.time, overrides.time)}
                            noWrap
                            align='right'
                            display='flex'
                            data-test={`card-time-${content?.friendlyUrl}'}`}
                          >
                            {/* `pluralize` keeps the empty playlist right too:
                                "0 Items", not the "0 Item" a `> 1` test gives.
                                The playlist info bar counts the same way. */}
                            <StackIcon size={18} weight='regular' /> {playlistItemCount} {pluralize('Item', playlistItemCount)}
                            {/* Read-time fragment only renders when the
                                playlist actually carries one — otherwise
                                the old code rendered a permanent Skeleton
                                that looked like a stuck loading state. */}
                            {contentTime && (
                              <>&nbsp;&middot;&nbsp;<Timer size={18} weight='regular' />{contentTime}</>
                            )}
                          </Typography>

                          )
                        : (
                          <Typography
                            variant='body1'
                            className={clsx(classes.time, overrides.time)}
                            noWrap
                            align='right'
                            data-test={`card-time-${content?.friendlyUrl}'}`}
                          >
                            {contentTime}
                          </Typography>
                          )}
                    </Grid>

                  </>
                )}
              </Grid>
              <Typography
                component={componentProps?.title ?? (link || content?.title ? 'h3' : 'div')}
                className={clsx(classes.title, overrides.title)}
                data-test={`card-title-${content?.friendlyUrl}'}`}
              >
                {link ?? (content?.title ?? <Skeleton width='80%' />)}
              </Typography>

              {/* MT.3: subtitle renders by default (useCardExpand keeps `expand`
                  true). Collapse stays wrapped for the rare caller that opts
                  back into hover-toggle, but the default behavior is always-on. */}
              <Collapse in={expand}>
                <Typography
                  variant='body2'
                  color='textSecondary'
                  className={clsx(classes.subtitle, overrides.subtitle)}
                  data-test={`card-subtitle-${content?.friendlyUrl}'}`}
                >
                  {content?.subtitle?.html ?? (
                    <>
                      <Skeleton width='100%' />
                      <Skeleton width='70%' />
                    </>
                  )}
                </Typography>
              </Collapse>
            </CardActionArea>

            {children}

            {/* Plain flex + gap (not MUI Grid spacing) so the action row
                shares the same left edge as the title — Grid's spacing
                implementation injects negative margins that pulled the
                buttons 8px outside the summary padding. */}
            <Box
              className={clsx(classes.actions, overrides.actions)}
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              flexWrap='nowrap'
              data-testid={`card-actions-${testId}`}
              data-test={`card-actions-${content?.friendlyUrl}'}`}
            >
              {content
                ? (
                  <>
                    <Box
                      display='flex'
                      alignItems='center'
                      gap={1}
                      flexWrap='nowrap'
                      data-test={`card-left-actions-${content?.friendlyUrl}'}`}
                    >
                      {actions?.leftActions}
                    </Box>
                    <Box
                      display='flex'
                      alignItems='center'
                      gap={1}
                      flexWrap='nowrap'
                      data-test={`card-right-actions-${content?.friendlyUrl}'}`}
                    >
                      {actions?.rightActions}
                    </Box>
                  </>
                  )
                : (
                  <Box ml={0.5} width='100%'>
                    <Skeleton variant='rectangular' width='40%' />
                  </Box>
                  )}
            </Box>
          </Box>
          {fullWidth &&
            right &&
            (content
              ? (
                <CardMedia
                  onClick={onSelect}
                  onKeyDown={handleImageKeyDown}
                  image={contentImage}
                  title={content?.image?.altText ?? `${content?.title} thumbnail`}
                  aria-label={content?.image?.altText ?? `${content?.title} thumbnail`}
                  className={classes.image}
                  style={{ width: 320, height: 176, flex: '0 0 320px' }}
                  data-testid={`card-image-${testId}`}
                  data-test={`card-image-${content?.friendlyUrl}`}
                  role='button'
                  tabIndex={0}
                />
                )
              : (
                <Skeleton variant='rectangular' className={classes.image} style={{ width: 320, height: 176, flex: '0 0 320px' }} />
                ))}
        </Card>
      </Stack>
    </Box>
  )
}

export default GeneralCard

GeneralCard.propTypes = {
  /** Content to be displayed on card */
  content: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.oneOf([false, undefined])
  ]),
  /** Buttons the user can click on to perform card related actions */
  actions: PropTypes.exact({
    leftActions: PropTypes.element,
    rightActions: PropTypes.element
  }),
  /** Should the card be expanded (show description)?  */
  expand: PropTypes.bool,
  /** Number of stacked borders the card should show  */
  stack: PropTypes.number,
  /** Child elements that appear below the card summary and action buttons  */
  children: PropTypes.node,
  /** Callback function fired when the card is selected  */
  onSelect: PropTypes.func
}
